import { useCallback } from 'react';
import * as Cesium from 'cesium';

export function useCameraControls(
  viewerRef: React.MutableRefObject<Cesium.Viewer | null>,
  secondaryViewerRef: React.MutableRefObject<Cesium.Viewer | null>,
  sharedDataSourceRef: React.MutableRefObject<any>,
  satelliteFollowSatIdRef: React.MutableRefObject<string | null>,
  satelliteFollowDistanceRef: React.MutableRefObject<number | null>,
  satelliteFollowTokenRef: React.MutableRefObject<number>,
  satelliteFollowRemoveRef: React.MutableRefObject<Cesium.Event.RemoveCallback | null>,
) {
  const getHeight = () => {
    const v = viewerRef.current;
    if (!v) return 9_500_000;
    return v.camera.positionCartographic.height;
  };

  const getSatelliteFollowCamera = useCallback((satEntity: Cesium.Entity, time: Cesium.JulianDate) => {
    const v = viewerRef.current;
    if (!v || !satEntity.position) return null;
    const pos = satEntity.position.getValue(time);
    if (!pos) return null;
    const distance = satelliteFollowDistanceRef.current ?? 28_000_000;
    const heading = Cesium.Math.toRadians(0);
    const pitch = Cesium.Math.toRadians(-60);
    const roll = 0;
    return {
      destination: Cesium.Cartesian3.add(
        pos,
        Cesium.Matrix3.multiplyByVector(
          Cesium.Matrix3.fromHeadingPitchRoll(new Cesium.HeadingPitchRoll(heading, pitch, roll)),
          new Cesium.Cartesian3(0, 0, distance),
          new Cesium.Cartesian3(),
        ),
        new Cesium.Cartesian3(),
      ),
      direction: Cesium.Cartesian3.normalize(
        Cesium.Cartesian3.negate(pos, new Cesium.Cartesian3()),
        new Cesium.Cartesian3(),
      ),
      up: new Cesium.Cartesian3(0, 0, 1),
    };
  }, []);

  const zoomIn = useCallback(() => {
    const v = viewerRef.current;
    if (!v) return;
    if (satelliteFollowSatIdRef.current) {
      satelliteFollowDistanceRef.current = Math.max((satelliteFollowDistanceRef.current ?? 28_000_000) / 1.35, 10_000_000);
      const ds = sharedDataSourceRef.current;
      const satEntity = ds?.entities.getById(satelliteFollowSatIdRef.current);
      const camera = satEntity ? getSatelliteFollowCamera(satEntity, v.clock.currentTime) : null;
      if (camera) {
        v.camera.setView({
          destination: camera.destination,
          orientation: { direction: camera.direction, up: camera.up },
        });
      }
      return;
    }
    v.camera.flyTo({ destination: Cesium.Cartesian3.fromDegrees(71, 21, getHeight() / 2), duration: 0.4 });
  }, [getSatelliteFollowCamera]);

  const zoomOut = useCallback(() => {
    const v = viewerRef.current;
    if (!v) return;
    if (satelliteFollowSatIdRef.current) {
      satelliteFollowDistanceRef.current = Math.min((satelliteFollowDistanceRef.current ?? 28_000_000) * 1.45, 120_000_000);
      const ds = sharedDataSourceRef.current;
      const satEntity = ds?.entities.getById(satelliteFollowSatIdRef.current);
      const camera = satEntity ? getSatelliteFollowCamera(satEntity, v.clock.currentTime) : null;
      if (camera) {
        v.camera.setView({
          destination: camera.destination,
          orientation: { direction: camera.direction, up: camera.up },
        });
      }
      return;
    }
    v.camera.flyTo({ destination: Cesium.Cartesian3.fromDegrees(71, 21, Math.min(getHeight() * 2, 120_000_000)), duration: 0.4 });
  }, [getSatelliteFollowCamera]);

  const resetZoom = useCallback(() => {
    const v = viewerRef.current;
    if (!v) return;
    satelliteFollowTokenRef.current += 1;
    satelliteFollowSatIdRef.current = null;
    satelliteFollowDistanceRef.current = null;
    if (satelliteFollowRemoveRef.current) {
      satelliteFollowRemoveRef.current();
      satelliteFollowRemoveRef.current = null;
    }
    v.camera.flyTo({ destination: Cesium.Cartesian3.fromDegrees(71, 21, 22_000_000), duration: 0.8 });
  }, []);

  const getZoomLevel = useCallback((): number => {
    const h = getHeight();
    return Math.max(1, Math.min(7, Math.round(Math.log10(2e7 / h) * 3.5 + 2)));
  }, []);

  const flyToOrbitView = useCallback((altKm = 22_000) => {
    const v = viewerRef.current;
    if (!v) return;
    v.camera.flyTo({ destination: Cesium.Cartesian3.fromDegrees(71, 21, altKm * 1000), duration: 1.4 });
  }, []);

  const centerOnSatellite = useCallback((satId: string) => {
    const v = viewerRef.current;
    const ds = sharedDataSourceRef.current;
    if (!v || !ds) return;
    const satEntity = ds.entities.getById(satId);
    if (!satEntity || !satEntity.position) return;
    const pos = satEntity.position.getValue(v.clock.currentTime);
    if (!pos) return;

    const cartographic = Cesium.Cartographic.fromCartesian(pos);
    if (!cartographic) return;
    const shiftedLon = cartographic.longitude - Cesium.Math.toRadians(15);
    const shiftedCartographic = new Cesium.Cartographic(shiftedLon, cartographic.latitude, cartographic.height);
    const shiftedPos = Cesium.Cartographic.toCartesian(shiftedCartographic);

    v.camera.flyTo({
      destination: Cesium.Cartesian3.add(
        shiftedPos,
        new Cesium.Cartesian3(0, 0, getHeight() / 4),
        new Cesium.Cartesian3(),
      ),
      duration: 1.0,
    });
  }, []);

  const centerOnPoint = useCallback((lon: number, lat: number) => {
    const v = viewerRef.current;
    if (!v) return;
    v.camera.flyTo({
      destination: Cesium.Cartesian3.fromDegrees(lon, lat, getHeight()),
      duration: 1.0,
    });
  }, []);

  const flyToSatellite = useCallback((satId: string) => {
    const v = viewerRef.current;
    const ds = sharedDataSourceRef.current;
    if (!v || !ds) return;
    const satEntity = ds.entities.getById(satId);
    if (!satEntity || !satEntity.position) return;
    const pos = satEntity.position.getValue(v.clock.currentTime);
    if (!pos) return;

    const cartographic = Cesium.Cartographic.fromCartesian(pos);
    if (!cartographic) return;
    const shiftedLon = cartographic.longitude - Cesium.Math.toRadians(15);
    const shiftedCartographic = new Cesium.Cartographic(shiftedLon, cartographic.latitude, cartographic.height);
    const shiftedPos = Cesium.Cartographic.toCartesian(shiftedCartographic);

    v.camera.flyToBoundingSphere(new Cesium.BoundingSphere(shiftedPos, 2500000.0), { duration: 1.5 });
  }, []);

  const syncSecondaryCamera = useCallback(() => {
    const v = viewerRef.current;
    const sv = secondaryViewerRef.current;
    if (!v || !sv) return;
    const cartographic = v.camera.positionCartographic;
    sv.camera.setView({
      destination: Cesium.Cartesian3.fromDegrees(
        Cesium.Math.toDegrees(cartographic.longitude),
        Cesium.Math.toDegrees(cartographic.latitude),
        9_500_000,
      ),
    });
  }, []);

  const viewWholeEarth2D = useCallback(() => {
    const sv = secondaryViewerRef.current;
    if (!sv) return;
    sv.camera.flyTo({
      destination: Cesium.Cartesian3.fromDegrees(0, 0, 35_000_000),
      duration: 1.0,
    });
  }, []);

  return {
    zoomIn,
    zoomOut,
    resetZoom,
    getZoomLevel,
    flyToOrbitView,
    centerOnSatellite,
    centerOnPoint,
    flyToSatellite,
    syncSecondaryCamera,
    viewWholeEarth2D,
    getSatelliteFollowCamera,
  };
}
